import jsPDF from 'jspdf';
import type { WhPrinterConfig } from '../types/manufacturing';

/**
 * Etichetta articolo con informazioni operative (richiesta Say Farm, impostazione
 * warehouse_settings.product_label_operational): al posto del codice a barre
 * il nome magazzino (uso interno) o il nome commerciale (ordini), Lotto Campo e
 * QR della pagina pubblica del prodotto.
 *
 * Usata dalla stampa etichetta della scheda articolo (ProductLabelPrint) e
 * dalla stampa etichette dell'ordine (OrderLabelsModal): stesso disegno, stesso
 * formato della stampante scelta (es. Brother QL-810W, rotoli da 62 mm).
 */

/** Uso dell'etichetta: interno (magazzino) o per gli ordini (nome commerciale) */
export type OperationalLabelUse = 'internal' | 'order';

/** Formato di stampa standard, senza stampante configurata */
export const STANDARD_LABEL_PAGE = { w: 100, h: 70 };

/** Pagina in mm della stampante, come le etichette dei lotti (LabelPrintModal). */
export function labelPageOf(printer?: WhPrinterConfig): { w: number; h: number } {
  if (!printer) return STANDARD_LABEL_PAGE;
  const w = Number(printer.paper_width_mm);
  const h = Number(printer.paper_height_mm);
  return printer.orientation === 'landscape' ? { w: h, h: w } : { w, h };
}

/**
 * Disposizione dell'etichetta in un formato qualsiasi: QR a destra se
 * l'etichetta è più larga che alta (100×70, 62×29), sotto se è stretta e alta
 * (rotoli Brother da 62 mm, es. 62×100). `scale` adatta i caratteri allo
 * spazio del testo (1 = colonna di 58×45 mm dell'etichetta 100×70).
 */
export function operationalLayout(page: { w: number; h: number }, hasQr: boolean) {
  const m = Math.max(2, Math.min(page.w, page.h) * 0.05);
  const side = page.w >= page.h * 1.15;
  const caption = 3;
  const q = !hasQr ? 0 : side ? Math.min(page.h - 2 * m - caption, page.w * 0.38) : Math.min(page.w - 2 * m, page.h * 0.38);
  const text = side
    ? { x: m, y: m, w: page.w - 2 * m - (q ? q + m : 0), h: page.h - 2 * m }
    : { x: m, y: m, w: page.w - 2 * m, h: page.h - 2 * m - (q ? q + caption + m : 0) };
  const qr = side ? { x: page.w - m - q, y: m, size: q } : { x: (page.w - q) / 2, y: page.h - m - caption - q, size: q };
  const scale = Math.min(1.3, Math.max(0.45, Math.min(text.w / 58, text.h / 45)));
  return { side, text, qr, caption, scale };
}

/** Contenuto di un'etichetta. */
export interface OperationalLabelContent {
  use: OperationalLabelUse;
  /** Nome magazzino (products.name) */
  productName: string;
  /** Nome commerciale; vuoto o uguale al nome = non stampato a parte */
  commercialName?: string | null;
  /** Magazzino «CODICE - Nome» (solo uso interno) */
  warehouse?: string | null;
  fieldLot?: string | null;
  /** QR della pagina pubblica già in PNG (generateQrPngDataUrl) */
  qrDataUrl?: string | null;
}

/** Testi stampati: interno nome magazzino (+ commerciale se diverso), ordine il nome commerciale. */
export function operationalLabelLines(c: OperationalLabelContent) {
  const commercial = c.commercialName?.trim() || '';
  const forOrder = commercial || c.productName;
  return {
    productTitle: c.use === 'internal' ? c.productName : forOrder,
    commercialLine: c.use === 'internal' && commercial && commercial !== c.productName ? commercial : '',
    warehouse: c.use === 'internal' ? c.warehouse?.trim() || '' : '',
    fieldLot: c.fieldLot?.trim() || '',
  };
}

/**
 * PDF con un'etichetta per pagina, nel formato `page`: per ogni voce
 * `copies` pagine uguali, nell'ordine dato.
 */
export function buildOperationalLabelsPdf(
  page: { w: number; h: number },
  labels: Array<{ content: OperationalLabelContent; copies: number }>,
): jsPDF {
  const orientation = page.w > page.h ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation, unit: 'mm', format: [page.w, page.h] });
  let first = true;

  for (const { content, copies } of labels) {
    const { text, qr, caption, scale } = operationalLayout(page, !!content.qrDataUrl);
    const bottom = text.y + text.h;
    const labelSize = Math.max(4.5, 7 * scale);
    const lines = operationalLabelLines(content);

    // Etichetta grigia + valore in grassetto, a capo nella colonna del testo;
    // le righe che non entrano nell'etichetta si tagliano
    const block = (y: number, label: string, value: string, size: number, maxLines: number) => {
      const fontSize = Math.max(5, size * scale);
      const lineHeight = fontSize * 0.42;
      const labelHeight = labelSize * 0.42;
      const room = Math.floor((bottom - y - labelHeight - 0.5) / lineHeight);
      if (room < 1) return y;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(labelSize);
      doc.setTextColor(110, 110, 110);
      doc.text(label.toUpperCase(), text.x, y + labelHeight);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
      doc.setTextColor(0, 0, 0);
      const rows = (doc.splitTextToSize(value, text.w) as string[]).slice(0, Math.min(maxLines, room));
      rows.forEach((line, i) => doc.text(line, text.x, y + labelHeight + 0.5 + lineHeight * (i + 1)));
      return y + labelHeight + 0.5 + lineHeight * rows.length + 3.5 * scale;
    };

    for (let i = 0; i < copies; i++) {
      if (!first) doc.addPage([page.w, page.h], orientation);
      first = false;
      let y = text.y;
      if (content.use === 'internal') {
        y = block(y, 'Prodotto', lines.productTitle, 11, 2);
        if (lines.commercialLine) y = block(y, 'Nome commerciale', lines.commercialLine, 9, 2);
        if (lines.warehouse) y = block(y, 'Magazzino', lines.warehouse, 15, 2);
      } else {
        y = block(y, 'Prodotto', lines.productTitle, 16, 3);
      }
      if (lines.fieldLot) block(y, 'Lotto campo', lines.fieldLot, 12, 1);

      if (content.qrDataUrl && qr.size > 0) {
        doc.addImage(content.qrDataUrl, 'PNG', qr.x, qr.y, qr.size, qr.size);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(Math.max(4.5, 6 * scale));
        doc.setTextColor(0, 0, 0);
        doc.text('Tracciabilità e documenti', qr.x + qr.size / 2, qr.y + qr.size + caption - 0.5, { align: 'center' });
      }
    }
  }
  return doc;
}

/**
 * Stampa diretta: il PDF, già nel formato della stampante, va alla finestra di
 * stampa del browser senza scaricarlo. Nella finestra resta da scegliere la
 * stampante (o niente, con Chrome avviato con --kiosk-printing e la Brother
 * come predefinita). Il driver deve avere lo stesso formato carta del rotolo.
 */
export function printPdfDocument(doc: jsPDF): void {
  const url = URL.createObjectURL(doc.output('blob'));
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      window.open(url, '_blank');
    }
  };
  document.body.appendChild(frame);
  setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
}
